import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useState } from 'react';
import type { HookMap } from '@toast-ui/editor';
import { pipe } from 'fp-ts/function';
import { ApplicationState, Option as OptionType } from '@mews-commander/types';
import {
    AppClockServiceProps,
    lt,
    showSuccessAlert,
    useDateTimeLocalizationInfo,
    useTimezone,
    withAppClockService,
} from '@mews/framework';
import { uploadTaskImage } from '@mews-commander/legacy-entities/src/actions/tasks';
import { useCommanderThunkDispatch, getDateTimeUtc } from '@mews-commander/utils';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { useEnterpriseNavigate } from '@mews-commander/ui';
import TasksForm from '../components/TasksForm';
import { getCurrentDepartmentsOption, getTaskFormInitialValues } from '../selectors';
import { FormValues } from '../types';

interface OwnProps {
    form: string;
    serviceOrderId?: string;
    scope: string;
}
interface StateProps {
    departments: Array<OptionType>;
    initialValues: FormValues;
}

const TaskAddForm = TasksForm({ updateTask: false });
const TaskAddFormConnected = (props: OwnProps & StateProps) => {
    const navigate = useEnterpriseNavigate();
    const timezone = useTimezone();
    const localizationInfo = useDateTimeLocalizationInfo();

    const dispatch = useCommanderThunkDispatch();
    const [imageIds, setImageIds] = useState<string[]>([]);

    const markdownEditorHooks: HookMap = {
        addImageBlobHook: async (blob, callback) => {
            const {
                value: { imageId, url },
            } = await dispatch(uploadTaskImage(blob));
            callback(url);
            setImageIds((state) => [...state, imageId]);
        },
    };
    const createTaskMutation = useRepositoryMutation('tasks', 'add', { invalidateQueries: { tasks: ['getAll'] } });

    const onSubmit = ({ date, time, ...formData }: FormValues) => {
        const deadlineUtc = getDateTimeUtc({ timezone, localizationInfo, date, time });
        createTaskMutation.mutate(
            { ...formData, deadlineUtc, imageIds: imageIds?.length ? imageIds : null },
            {
                onSuccess: async (data) => {
                    showSuccessAlert({ description: lt('SaveSuccessMessage') });
                    await navigate({ to: 'Task/Detail/$taskId', params: { taskId: data.id }, replace: true });
                },
            }
        );
    };
    return <TaskAddForm {...props} markdownEditorHooks={markdownEditorHooks} onSubmit={onSubmit} />;
};
export default pipe(
    TaskAddFormConnected,
    connect<StateProps, {}, OwnProps & AppClockServiceProps, ApplicationState>(
        createStructuredSelector({
            departments: getCurrentDepartmentsOption,
            initialValues: getTaskFormInitialValues,
        })
    ),
    withAppClockService
);
